import { useEffect, useRef } from "react";

interface IParticle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  r: number;
}

const GREEN = "0,200,137";
const PINK = "226,140,150";
const MOBILE_WIDTH = 768;
const POINTER_PULL_RADIUS = 180;
const POINTER_LINK_RADIUS = 200;

const NetworkBackground = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !ctx) return undefined;

    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const pointer = { x: -9000, y: -9000 };
    let width = 0;
    let height = 0;
    let linkDistance = 150;
    let linkAlpha = 0.42;
    let particles: IParticle[] = [];
    let frameId = 0;

    const resize = (force: boolean) => {
      const w = window.innerWidth;
      const h = window.innerHeight;
      if (!force && w === width && Math.abs(h - height) < 120) return;
      width = w;
      height = h;
      const ratio = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = width * ratio;
      canvas.height = height * ratio;
      ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
      const mobile = width < MOBILE_WIDTH;
      linkDistance = mobile ? 135 : 150;
      linkAlpha = mobile ? 0.62 : 0.42;
      let count = Math.round(Math.min(90, (width * height) / 16000));
      if (mobile) count = Math.max(count, 46);
      const speed = mobile ? 0.5 : 0.35;
      particles = Array.from({ length: count }, () => ({
        x: Math.random() * width,
        y: Math.random() * height,
        vx: (Math.random() - 0.5) * speed,
        vy: (Math.random() - 0.5) * speed,
        r: Math.random() * 1.6 + 1.4,
      }));
    };

    const drawLine = (x1: number, y1: number, x2: number, y2: number, color: string) => {
      ctx.strokeStyle = color;
      ctx.beginPath();
      ctx.moveTo(x1, y1);
      ctx.lineTo(x2, y2);
      ctx.stroke();
    };

    const frame = () => {
      ctx.clearRect(0, 0, width, height);
      particles.forEach((a, i) => {
        if (!reducedMotion) {
          a.x += a.vx;
          a.y += a.vy;
          if (a.x < 0 || a.x > width) a.vx *= -1;
          if (a.y < 0 || a.y > height) a.vy *= -1;
          const px = pointer.x - a.x;
          const py = pointer.y - a.y;
          if (Math.hypot(px, py) < POINTER_PULL_RADIUS) {
            a.x += px * 0.004;
            a.y += py * 0.004;
          }
        }
        for (let j = i + 1; j < particles.length; j++) {
          const b = particles[j];
          const d = Math.hypot(a.x - b.x, a.y - b.y);
          if (d < linkDistance) {
            drawLine(a.x, a.y, b.x, b.y, `rgba(${PINK},${linkAlpha * (1 - d / linkDistance)})`);
          }
        }
        const dp = Math.hypot(a.x - pointer.x, a.y - pointer.y);
        if (dp < POINTER_LINK_RADIUS) {
          drawLine(
            a.x,
            a.y,
            pointer.x,
            pointer.y,
            `rgba(${GREEN},${0.45 * (1 - dp / POINTER_LINK_RADIUS)})`,
          );
        }
        ctx.fillStyle = i % 4 === 0 ? `rgba(${GREEN},.8)` : `rgba(${PINK},.8)`;
        ctx.beginPath();
        ctx.arc(a.x, a.y, a.r, 0, Math.PI * 2);
        ctx.fill();
      });
      if (!reducedMotion) frameId = window.requestAnimationFrame(frame);
    };

    const onResize = () => {
      resize(false);
      if (reducedMotion) frame();
    };
    const onMouseMove = (e: MouseEvent) => {
      pointer.x = e.clientX;
      pointer.y = e.clientY;
    };
    const onTouchMove = (e: TouchEvent) => {
      const touch = e.touches[0];
      if (touch) {
        pointer.x = touch.clientX;
        pointer.y = touch.clientY;
      }
    };

    resize(true);
    window.addEventListener("resize", onResize);
    window.addEventListener("mousemove", onMouseMove, { passive: true });
    window.addEventListener("touchmove", onTouchMove, { passive: true });
    frame();

    return () => {
      window.cancelAnimationFrame(frameId);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("touchmove", onTouchMove);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      data-testid="login-network-background"
      aria-hidden="true"
      className="fixed inset-0 w-full h-full pointer-events-none -z-10 block"
    />
  );
};

export default NetworkBackground;
